import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { useCallback, useSyncExternalStore } from "react";

import { apiGet, apiPatch, mockFor, mockResponse } from "./client";
import type { OfficeAuditEntry } from "./types";

export interface OfficeMapping {
  officeName: string;
  officeNumbers: string[];
  liveOnAthena: boolean;
  goLiveDate: string | null;
  note: string | null;
  lastUpdatedBy: string | null;
  lastUpdatedAt: string | null;
}

export type OfficePatch = Partial<Pick<OfficeMapping, "liveOnAthena" | "goLiveDate" | "note">>;

interface StoreState {
  offices: OfficeMapping[];
  auditLog: OfficeAuditEntry[];
}

function nowIso() {
  return new Date().toISOString();
}

function seed(): StoreState {
  const goLive = format(subDays(new Date(), 90), "yyyy-MM-dd");
  const seededAt = subDays(new Date(), 92).toISOString();

  const offices: OfficeMapping[] = [
    {
      officeName: "Dallas",
      officeNumbers: ["101", "104"],
      liveOnAthena: true,
      goLiveDate: goLive,
      note: "First office live on Athena 2.0; all baseline metrics anchor here.",
      lastUpdatedBy: "rollout-admin",
      lastUpdatedAt: seededAt,
    },
    {
      officeName: "Atlanta",
      officeNumbers: ["205"],
      liveOnAthena: false,
      goLiveDate: null,
      note: "Rollout pending — LeaseWorks mapping in review.",
      lastUpdatedBy: null,
      lastUpdatedAt: null,
    },
    {
      officeName: "Chicago",
      officeNumbers: ["310", "312"],
      liveOnAthena: false,
      goLiveDate: null,
      note: "Rollout pending — credit policy alignment outstanding.",
      lastUpdatedBy: null,
      lastUpdatedAt: null,
    },
    {
      officeName: "Phoenix",
      officeNumbers: ["418"],
      liveOnAthena: false,
      goLiveDate: null,
      note: "Rollout pending — no go-live date set.",
      lastUpdatedBy: null,
      lastUpdatedAt: null,
    },
    {
      officeName: "Charlotte",
      officeNumbers: ["522", "527"],
      liveOnAthena: false,
      goLiveDate: null,
      note: "Rollout pending — awaiting underwriter onboarding.",
      lastUpdatedBy: null,
      lastUpdatedAt: null,
    },
  ];

  const auditLog: OfficeAuditEntry[] = [
    {
      entryId: "OAE-002",
      officeName: "Dallas",
      fieldChanged: "liveOnAthena",
      oldValue: "false",
      newValue: "true",
      changedBy: "rollout-admin",
      changedAt: seededAt,
    },
    {
      entryId: "OAE-001",
      officeName: "Dallas",
      fieldChanged: "goLiveDate",
      oldValue: "—",
      newValue: goLive,
      changedBy: "rollout-admin",
      changedAt: subDays(new Date(), 110).toISOString(),
    },
  ];

  return { offices, auditLog };
}

let state: StoreState = seed();
let counter = 100;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): StoreState {
  return state;
}

function display(value: string | boolean | null | undefined) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function updateOffice(officeName: string, patch: OfficePatch, changedBy: string) {
  const office = state.offices.find((o) => o.officeName === officeName);
  if (!office) return;

  const changedAt = nowIso();
  const entries: OfficeAuditEntry[] = [];
  const fields: (keyof OfficePatch)[] = ["liveOnAthena", "goLiveDate", "note"];

  for (const field of fields) {
    if (!(field in patch)) continue;
    const next = patch[field] ?? null;
    const prev = office[field] ?? null;
    if (next === prev) continue;
    counter += 1;
    entries.push({
      entryId: `OAE-${counter}`,
      officeName,
      fieldChanged: field,
      oldValue: display(prev),
      newValue: display(next),
      changedBy,
      changedAt,
    });
  }

  if (entries.length === 0) return;

  const offices = state.offices.map((o) =>
    o.officeName === officeName
      ? {
          ...o,
          // goLiveDate is deliberately preserved when going non-live.
          ...patch,
          lastUpdatedBy: changedBy,
          lastUpdatedAt: changedAt,
        }
      : o,
  );

  state = { offices, auditLog: [...entries.reverse(), ...state.auditLog] };
  emit();
}

/**
 * Constant for the life of the process, so the hooks below can call both the
 * store and the query unconditionally and still branch on it — hook order stays
 * stable either way.
 */
const MOCK = mockFor("/office-mapping");

const OFFICES_KEY = ["office-mapping"] as const;
const AUDIT_KEY = ["office-audit-log"] as const;

export async function fetchOfficeMapping(): Promise<OfficeMapping[]> {
  if (MOCK) return mockResponse(state.offices);
  return apiGet<OfficeMapping[]>("/office-mapping");
}

async function fetchOfficeAuditLog(): Promise<OfficeAuditEntry[]> {
  if (MOCK) return mockResponse(state.auditLog);
  return apiGet<OfficeAuditEntry[]>("/office-mapping/audit-log");
}

/**
 * Same return shape as before (`{ data }`).
 *
 * In mock mode this reads the in-module store synchronously, as it always has.
 * Against a real backend it is a normal query, so `data` is undefined on the
 * first render — which is exactly what the `enabled: Boolean(offices)` guard in
 * every dependent hook was already written to handle.
 */
export function useOfficeMapping() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const query = useQuery({
    queryKey: OFFICES_KEY,
    queryFn: fetchOfficeMapping,
    enabled: !MOCK,
    staleTime: 60 * 1000,
  });

  if (MOCK) {
    return {
      data: snapshot.offices as OfficeMapping[] | undefined,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  return {
    data: query.data,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
  };
}

export function useOfficeRolloutActions() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const queryClient = useQueryClient();
  const { data: liveOfficeList } = useOfficeMapping();
  const auditQuery = useQuery({
    queryKey: AUDIT_KEY,
    queryFn: fetchOfficeAuditLog,
    enabled: !MOCK,
    staleTime: 60 * 1000,
  });

  const update = useCallback(
    async (officeName: string, patch: OfficePatch, changedBy: string) => {
      if (MOCK) {
        updateOffice(officeName, patch, changedBy);
        return;
      }
      await apiPatch<OfficeMapping>(
        `/office-mapping/${encodeURIComponent(officeName)}`,
        { ...patch, changedBy },
      );
      // Refreshing the office list is what makes every OTHER page pick the
      // change up: officesFingerprint feeds their query keys, so a new rollout
      // state rekeys them and they refetch on their own.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: OFFICES_KEY }),
        queryClient.invalidateQueries({ queryKey: AUDIT_KEY }),
      ]);
    },
    [queryClient],
  );

  return {
    offices: MOCK ? snapshot.offices : liveOfficeList ?? [],
    auditLog: MOCK ? snapshot.auditLog : auditQuery.data ?? [],
    updateOffice: update,
  };
}

/** Changes whenever rollout state changes, so dependent query keys invalidate. */
export function officesFingerprint(offices: OfficeMapping[] | undefined) {
  if (!offices) return "none";
  return offices
    .map((o) => `${o.officeName}:${o.liveOnAthena ? 1 : 0}:${o.goLiveDate ?? ""}`)
    .join("|");
}

/**
 * Has the office list finished resolving — successfully or not?
 *
 * Dependent queries gate on this rather than on `Boolean(offices)`. The difference
 * only shows up when the office list *fails*: a query left `enabled: false` stays
 * `isPending` forever, and every page reads that as "loading" and renders skeletons
 * indefinitely. Letting the dependent query run instead means it hits the same
 * broken backend and surfaces a real error card, which is the truth.
 *
 * In mock mode `offices` is always defined, so this is unchanged from before.
 */
export function officesSettled(
  offices: OfficeMapping[] | undefined,
  officesFailed: boolean,
) {
  return offices !== undefined || officesFailed;
}

export function liveOffices(offices: OfficeMapping[] | undefined) {
  return (offices ?? []).filter((o) => o.liveOnAthena);
}

export function isAllOffices(office: string | undefined) {
  return !office || office === "all" || office === "All Offices";
}

/**
 * Generic liveness gate: data exists for "All Offices" whenever at least one
 * office is live, and for a named office only when that office is live.
 */
export function officeDataAvailable(
  offices: OfficeMapping[] | undefined,
  office: string | undefined,
) {
  const live = liveOffices(offices);
  if (isAllOffices(office)) return live.length > 0;
  return live.some((o) => o.officeName === office);
}
